/** @format */
/* eslint-disable no-useless-escape */

import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, DOCUMENT, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { sha256 } from 'js-sha256';
import { v4 as uuidv4 } from 'uuid';
import { REQUEST } from '@shared/data-access/constants';
import { DateTime } from 'luxon';
import { Chain, KillSwitches, ChainIdentifierAbbr } from '@shared/data-access/interfaces';

import { WINDOW } from './window/window.service';
import { convertToNumber, isTruthy } from './utility-functions';

type PreloadAsType = 'fetch' | 'font' | 'image' | 'script' | 'style';
type PreloadCrossOriginType = 'anonymous' | 'use-credentials';

/** @dynamic */
@Injectable({
  providedIn: 'root',
})
export class UtilityService {
  private readonly chain = inject(Chain);
  private readonly document = inject(DOCUMENT);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly window = inject(WINDOW);
  private readonly request = inject(REQUEST, { optional: true });

  readonly ERROR_MESSAGES = {
    geolocation: {
      geolocationUnavailable: 'Geolocation API is not available in this browser',
    },
  };

  /**
   * @description
   * Accepts a string to be run through regex and returned
   */
  static cleanString(value, pattern?: string | RegExp, flags = 'gi'): string {
    if (value === undefined) {
      return '';
    }

    // Use provided pattern
    if (pattern !== undefined) {
      return value.replace(new RegExp(pattern, flags), '');
    }

    // Default to stripping all special characters
    return value.replaceAll(/[\s!@#$%^*(){}\[\];'",<>.\/\\|?_+=`~‘’“”…′\-]/g, '');
  }

  static matchObjectArrayParameter(arr1: object[], obj: object, property: string) {
    const getPropertyValue = (value: object): unknown =>
      Object.entries(value).find(([key]) => key === property)?.[1];

    const objectValue = getPropertyValue(obj);

    return arr1.some((item) => getPropertyValue(item) === objectValue);
  }

  static mapValuesFromOneArrayToAnother<SourceItem extends object, TargetItem extends object>(
    sourceItems: readonly SourceItem[],
    targetItems: TargetItem[],
    matchProperty: string,
  ): TargetItem[] {
    const getPropertyValue = (value: object): unknown =>
      Object.entries(value).find(([key]) => key === matchProperty)?.[1];

    for (const sourceItem of sourceItems) {
      const sourceValue = getPropertyValue(sourceItem);

      if (sourceValue) {
        for (const targetItem of targetItems) {
          const targetValue = getPropertyValue(targetItem);

          if (targetValue === sourceValue) {
            Object.assign(targetItem, sourceItem);
          }
        }
      }
    }

    return targetItems;
  }

  target(link: string) {
    if (link) {
      return link.indexOf('://') > 0 || link.startsWith('//') ? '_blank' : '_self';
    } else {
      return '_self';
    }
  }

  /**
   * @returns The new Date
   *
   * @description
   * Adds a specified number of days to a given Date and returns a new Date
   */
  addDaysToDate(date, days): Date {
    // Create a new Date based on the given Date
    const newDate = new Date(date);

    // Add the specified number od days
    newDate.setDate(newDate.getDate() + days);

    // Return the new Date
    return newDate;
  }

  /**
   * @description
   * Accepts an argument and attempts to convert its value to a number
   */
  convertToNumber(number: unknown, radix = 10): number | undefined {
    return convertToNumber(number, radix);
  }

  /**
   * @returns A RFC4122 v4 UUID/GUID string
   *
   * @description
   * Method that generates a high-fidelity UUID/GUID. It uses an NPM module `uuid` to generate a RFC4122 v4
   * UUID/GUID string.
   */
  generateUUID(): string {
    // Return UUID/GUID
    return uuidv4();
  }

  /**
   * @returns true or false if the input string is a UUID
   *
   * @description
   * Method that validates whether a string is in a valid UUID format. It uses a regex to validate the string.
   */
  isValidUUID(value: string): boolean {
    return !(
      value.length > 0 &&
      !/^\{?[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}}?$/.test(
        value,
      )
    );
  }

  /**
   * @description
   * Attempts to get the current geolocation position
   *
   * @returns The current geolocation if available, otherwise undefined
   */
  getCurrentGeolocationPosition(): Observable<GeolocationPosition> {
    // Return an Observable
    return new Observable((observer) => {
      // Check to ensure that gelocation API is available in the browser
      if (this.window.navigator.geolocation) {
        // Attempt to get the current location
        this.window.navigator.geolocation.getCurrentPosition(
          (currentPosition: GeolocationPosition) => {
            // Return the current location
            observer.next(currentPosition);
            observer.complete();
          },
          (error) => {
            console.error(
              'UtilityService.getCurrentGeolocationPosition -- Unable to get current geolocation position: ',
              JSON.stringify(error, ['message']),
            );

            // Throw error
            observer.error(new Error(JSON.stringify(error, ['message'])));
          },
        );
      } else {
        console.warn(
          'UtilityService.getCurrentGeolocationPosition -- Geolocation API is not available in this browser!',
        );

        // Throw error
        observer.error(new Error(this.ERROR_MESSAGES.geolocation.geolocationUnavailable));
      }
    });
  }

  /**
   * @description
   * Accepts an Element and gets its complete height
   */
  getElementHeight(
    element: Element,
    includeTopMarginAndPadding = false,
    includeBottomMarginAndPadding = false,
  ): number {
    // Get the current computed styles
    const currentComputedStyles = this.window.getComputedStyle(element, null);

    // Set the initial height
    let height = Number.parseInt(
      currentComputedStyles.getPropertyValue('height').replace('px', ''),
      10,
    );

    // Check to see if top margins should be included
    if (includeTopMarginAndPadding) {
      height += Number.parseInt(
        currentComputedStyles.getPropertyValue('margin-top').replace('px', ''),
        10,
      );
      height += Number.parseInt(
        currentComputedStyles.getPropertyValue('padding-top').replace('px', ''),
        10,
      );
    }

    // Check to see if top margins should be included
    if (includeBottomMarginAndPadding) {
      height += Number.parseInt(
        currentComputedStyles.getPropertyValue('margin-bottom').replace('px', ''),
        10,
      );
      height += Number.parseInt(
        currentComputedStyles.getPropertyValue('padding-bottom').replace('px', ''),
        10,
      );
    }

    // Return the element height
    return height;
  }

  /**
   * @description
   * Accepts a string and hashes it using SHA-256 algorithm
   */
  hashString(stringToHash: string): string {
    // Ensure the argument is a valid string
    if (typeof stringToHash === 'string' && stringToHash.trim() !== '') {
      const hash = sha256.create();

      stringToHash = stringToHash.trim().toLowerCase();

      hash.update(stringToHash);

      return hash.hex().toUpperCase();
    }

    // Return empty string by default
    return '';
  }

  /**
   * @description
   * Accepts a string and an array of strings and checks to see if the string exists in the array
   */
  isItemInList(itemToCheck: string, itemsToCheckAgainst: string[]): boolean {
    // Ensure there is enough to compare
    if (
      typeof itemToCheck === 'string' &&
      itemToCheck.trim() !== '' &&
      Array.isArray(itemsToCheckAgainst) &&
      itemsToCheckAgainst.length > 0
    ) {
      // Lowercase the string to check for comparison
      itemToCheck = itemToCheck.toLowerCase();

      // Iterate the items to check against
      for (let index = 0, length = itemsToCheckAgainst.length; index < length; index += 1) {
        if (
          typeof itemsToCheckAgainst[index] === 'string' &&
          itemsToCheckAgainst[index].toLowerCase() === itemToCheck
        ) {
          // Return match found
          return true;
        }
      }
    }

    // There was not a match, return false
    return false;
  }

  /**
   * @description
   * Method that checks whether a given argument is 'truthy' which means it will check multiple types and values which
   * would evaluate to `true`. It will return `false` by default.
   */
  isTruthy(inArgument: unknown): boolean {
    return isTruthy(inArgument);
  }

  /**
   * @description
   * Accepts a string and determines if it is of type string and not empty
   */
  isValidString(stringToCheck): boolean {
    // Return value
    let returnValue = false;

    // Check the string
    if (typeof stringToCheck === 'string' && stringToCheck.trim().length > 0) {
      // Flip the flag
      returnValue = true;
    }

    // Return the return value
    return returnValue;
  }

  /**
   * @description
   * Loads a specified style sheet into the head of the DOM.  If a before argument is provided, it will insert before a
   * specified node by ID otherwise will default to the first script element.  If a media argument is provided,
   * it will add the media attribute of that type.
   *
   * @param href - The URL of the stylesheet
   * @param id - The ID of the stylesheet
   * @param [before] - The HTML Element to insert the stylesheet before
   * @param [callback] - Callback function to execute once the stylesheet is defined
   * @param [media] - The media type to apply the stylesheet for
   */
  /* istanbul ignore next */
  loadStyleSheet(
    href: string,
    id: string,
    before?: HTMLElement,
    callback?: () => void,
    media?: string,
  ) {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    const DOMElement = this.document.getElementById(id);

    if (DOMElement) {
      return;
    }

    // Fallback
    callback =
      callback ||
      function () {
        // Empty callback
      };

    // Create a stylesheet link element
    const styleSheetLink = this.document.createElement('link');

    // Get the reference element to insert before
    const referenceElement = before || this.document.getElementsByTagName('script')[0];

    // Get all of the style sheets
    const sheets = this.window.document.styleSheets;

    styleSheetLink.id = `${id}`;

    // Set the `rel` attribute to 'stylesheet'
    styleSheetLink.rel = 'stylesheet';

    // Set the `href` attribute
    styleSheetLink.href = href;

    // temporarily, set the `media` attribute to something non-matching to ensure it'll fetch without blocking render
    styleSheetLink.media = 'only x';

    // Set up an interval to poll the document.readyState value
    const interval = setInterval(() => {
      // Check to see if the document has completed loading
      if (this.document.readyState === 'complete') {
        // Document has completed loading, clear the polling interval
        clearInterval(interval);

        // Check to see if there is a valid reference element
        if (referenceElement?.parentNode !== undefined && referenceElement.parentNode !== null) {
          // Insert the style sheet before the reference
          referenceElement.parentNode.insertBefore(styleSheetLink, referenceElement);
        } else {
          this.document.head.appendChild(styleSheetLink);
        }
      }
    }, 100);

    /*
     * This function sets the link's media back to `all` so that the stylesheet applies once it loads.
     * It is designed to poll until document.styleSheets includes the new sheet.
     */
    const onLoadCssDefined = (cb) => {
      // Flag to determine if the stylesheet is defined in the DOM
      let defined;

      // Iterate through the stylesheets
      for (let i = 0, length = sheets.length; i < length; i++) {
        // Check to see if the current stylesheet href matches ours
        if (sheets[i].href && sheets[i].href === styleSheetLink.href) {
          // Set the flag to true
          defined = true;
        }
      }

      // Check to see if the new stylesheet is defined in the DOM
      if (defined) {
        // Perform the callback
        cb();
      } else {
        // Set a small timeout
        setTimeout(() => {
          // Poll the stylesheets
          onLoadCssDefined(cb);
        }, 50);
      }
    };

    // Call the onload function with a callback to set the media type so that the stylesheet applies
    onLoadCssDefined(() => {
      // Set the `media` attribute to a matching value
      styleSheetLink.media = media || 'screen';

      // Call the callback function
      callback();
    });
  }

  /**
   * @description
   * Loads an external JavaScript file into the DOM and will then call an optionally supplied callback if provided
   */
  loadExternalScript(
    url: string,
    onLoadCallback: () => void = () => {
      /* empty default */
    },
    onErrorCallback: () => void = () => {
      /* empty default */
    },
    appendHead = false,
    isAnonymous = true,
    extraConfig?: {
      integrity?: string;
      onTimeoutCallback?: () => void;
      timeoutMilliseconds?: number;
    },
  ): void {
    const {
      integrity,
      onTimeoutCallback,
      timeoutMilliseconds = 10000,
    } = extraConfig ?? { timeoutMilliseconds: 10000 };

    const timeout = onTimeoutCallback
      ? setTimeout(() => {
          onTimeoutCallback();
        }, timeoutMilliseconds)
      : null;

    // Create a new <script> Element
    const script = this.document.createElement('script');

    // Set the `async` attribute
    script.setAttribute('async', '');

    // Set the `type` attribute
    script.setAttribute('type', 'text/javascript');

    // Set 'crossOrigin' attribute
    if (isAnonymous) {
      script.setAttribute('crossOrigin', 'anonymous');
    }

    script.onload = () => {
      if (timeout) {
        clearTimeout(timeout);
      }

      // Call the onLoadCallback
      onLoadCallback();
    };

    script.onerror = () => {
      // Call error callback
      onErrorCallback();
    };

    // Set the `src` attribute
    script.setAttribute('src', url);

    // Set the `integrity` attribute
    if (integrity) {
      script.setAttribute('integrity', integrity);
    }

    if (appendHead) {
      this.document.head.appendChild(script);
    } else {
      // Append the Element to the bottom of the body
      this.document.body.appendChild(script);
    }
  }

  /**
   * @description
   * Loads an external JavaScript file into the DOM and will then call an optionally supplied callback if provided
   */
  loadExternalScriptWithIntegrity(
    url: string,
    integrity: string,
    onLoadCallback: () => void = () => {
      /* empty default */
    },
    onErrorCallback: () => void = () => {
      /* empty default */
    },
    appendHead = false,
  ): void {
    this.loadExternalScript(url, onLoadCallback, onErrorCallback, appendHead, true, { integrity });
  }

  /**
   * @description
   * Loads an external JavaScript file into the DOM and will then call an optionally supplied callback if provided
   */
  public loadExternalScriptWithIntegrityAndTimeout(
    url: string,
    integrity: string,
    onLoadCallback: () => void = () => {
      /* empty default */
    },
    onErrorCallback: () => void = () => {
      /* empty default */
    },
    onTimeoutCallback: () => void = () => {
      /* empty default */
    },
    appendHead = false,
    timeoutMilliseconds = 10000,
  ): void {
    this.loadExternalScript(url, onLoadCallback, onErrorCallback, appendHead, true, {
      integrity,
      onTimeoutCallback,
      timeoutMilliseconds,
    });
  }

  /**
   * @description
   * takes an element and calls scrollIntoView with options
   */
  scrollToElement(
    element: HTMLElement | undefined | null,
    options?: ScrollIntoViewOptions,
    offset?: number,
  ): boolean {
    const defaults = {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    } as ScrollIntoViewOptions;

    options = options || defaults;

    if (element !== undefined && element !== null) {
      if (offset) {
        const { container, insideFixedOverlay } = this.resolveScrollTarget(element);
        if (container) {
          const containerTop = container.getBoundingClientRect().top;
          const elementTop = element.getBoundingClientRect().top;
          const target = container.scrollTop + (elementTop - containerTop) - offset;

          container.scrollTo({
            top: Math.max(0, target),
            behavior: options.behavior,
          });

          return true;
        }

        if (insideFixedOverlay) {
          return true;
        }

        const scrollY = this.window.scrollY;
        const elementY = element.getBoundingClientRect().top;

        this.window.scrollTo({
          top: scrollY + elementY - offset,
          behavior: options.behavior,
        });

        return true;
      }

      element.scrollIntoView({
        behavior: options.behavior,
        block: options.block,
        inline: options.inline,
      });

      return true;
    }

    return false;
  }

  /**
   * @description Walks up from the given element once, returning the nearest
   * scrollable container (if any) and whether the element lives inside a
   * position:fixed overlay. Callers use this to scroll a container or a fixed
   * overlay's viewport instead of the window, which keeps the page behind an
   * open overlay static.
   */
  private resolveScrollTarget(element: HTMLElement): {
    container: HTMLElement | null;
    insideFixedOverlay: boolean;
  } {
    let node = element.parentElement;
    let insideFixedOverlay = false;

    while (node) {
      const { overflowY, position } = this.window.getComputedStyle(node);

      if (position === 'fixed') {
        insideFixedOverlay = true;
      }

      if (
        (overflowY === 'auto' || overflowY === 'scroll') &&
        node.scrollHeight > node.clientHeight
      ) {
        return { container: node, insideFixedOverlay };
      }

      node = node.parentElement;
    }

    return { container: null, insideFixedOverlay };
  }

  /**
   * @description
   * scrolls to the top of the page
   * supply options to override smooth behavior
   */
  scrollToTop(options?: ScrollToOptions) {
    const defaults = {
      top: 0,
      behavior: 'smooth',
    } as ScrollToOptions;

    options = options || defaults;

    window.scrollTo(options);
  }

  /**
   * @description
   * check to see if browser supports IntersectionObserver
   */
  hasIntersectionObserverSupport(): boolean {
    const hasIntersectionObserver = 'IntersectionObserver' in this.window;
    const userAgent = this.window.navigator.userAgent;
    const matches = userAgent.match(/Edge\/(\d*)\./i);
    const isEdge = !!matches && matches.length > 1;
    const isEdgeVersion16OrBetter = isEdge && !!matches && Number.parseInt(matches[1], 10) > 15;

    return hasIntersectionObserver && (!isEdge || isEdgeVersion16OrBetter);
  }

  private getWindowKillSwitchValue(killSwitchName: string): boolean | undefined {
    const value: unknown = Reflect.get(this.window, killSwitchName);
    return typeof value === 'boolean' ? value : undefined;
  }

  /**
   * @description
   * check to see if killswitch is enabled.
   * override takes precedence over window and killswitch if provided
   * @param killSwitchName
   * @param killSwitch
   * @param chainIdentifierAbbr
   */
  checkKillSwitch(
    killSwitchName: string,
    killSwitch: KillSwitches,
    chainIdentifierAbbr: ChainIdentifierAbbr,
  ): boolean {
    const overrideName = `${killSwitchName}Override`;
    const override =
      killSwitch?.[overrideName] !== undefined && killSwitch?.[overrideName] !== null
        ? killSwitch[overrideName][chainIdentifierAbbr]
        : undefined;

    if (override !== undefined) {
      return override;
    } else if (this.getWindowKillSwitchValue(killSwitchName) !== undefined) {
      return !!this.getWindowKillSwitchValue(killSwitchName);
    } else if (killSwitch?.[killSwitchName] !== undefined) {
      return !!killSwitch[killSwitchName][chainIdentifierAbbr];
    }

    return false;
  }

  /**
   * @returns {Boolean}
   *
   * @description
   * Used to check if a property passed from api is array containing only empty object
   * @example
   * // isObjectInArrayEmpty([{}],0) returns true
   */

  isObjectInArrayEmpty<T extends object>(array: T[]): boolean {
    return Object.keys(array[0]).length === 0;
  }

  /**
   * @returns {Boolean}
   *
   * @description
   * Used to check if the current page is a GG Static Page
   */

  ggStaticPage(isStagePage: boolean): boolean {
    return isStagePage && this.chain.chainIdentifierAbbr === 'gg';
  }

  convertSizeChartNameToNewFormat(sizeChartName: string): string {
    return `sc-${sizeChartName
      .toLowerCase()
      .replace(/size|chart|content|_/gi, '')
      .replace(' ', '-')}`;
  }

  removeWhiteSpace(str: string) {
    return str.trim().replace(/\s/g, '');
  }

  /**
   * @description Compares if the passed in date is after the current date. Uses `luxon` instead of the browser's own date parser to ensure broad support.
   * @param date example format: 08-03-2022
   * @param format date format string to use when parsing. See https://moment.github.io/luxon/#/parsing?id=table-of-tokens for syntax
   * @returns boolean representing if passed in date is greater than current date.
   */
  static isDateAfterNowDate(date: string | undefined, format: string): boolean {
    if (date === undefined) {
      return false;
    }
    const newDate = DateTime.fromFormat(date, format);
    if (!newDate.isValid) {
      throw new Error(
        `Invalid date input: ${newDate.invalidReason}: ${newDate.invalidExplanation}`,
      );
    }
    return newDate.startOf('day') >= DateTime.now();
  }

  private isIndexableObject(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  findPropertyInObject(obj: unknown, propertyName: string): unknown {
    if (Array.isArray(obj)) {
      for (const value of obj) {
        const result = this.findPropertyInObject(value, propertyName);

        if (result !== null) {
          return result;
        }
      }
    }

    if (!this.isIndexableObject(obj)) {
      return null;
    }

    if (propertyName in obj) {
      return obj[propertyName];
    }

    for (const value of Object.values(obj)) {
      const result = this.findPropertyInObject(value, propertyName);

      if (result !== null) {
        return result;
      }
    }

    return null;
  }

  setFallbackImage(event: Event, fallbackImage: string): void {
    const target = event.target as HTMLImageElement;
    target.setAttribute('src', fallbackImage);
  }

  /**
   * @description
   * Adds a preload tag for a resource file with high priority if it doesn't already exist.
   */
  insertPreloadTag(
    src: string,
    type: PreloadAsType = 'script',
    crossOrigin?: PreloadCrossOriginType,
  ): void {
    // Cache the head element reference
    const head = this.document.head;

    // Check if a preload link with the same href already exists
    if (!head.querySelector(`link[rel="preload"][href="${src}"]`)) {
      // Create the preload link element
      const preloadLink = this.document.createElement('link');
      preloadLink.setAttribute('fetchpriority', 'high');
      preloadLink.rel = 'preload';
      preloadLink.as = type;
      preloadLink.href = src;

      if (crossOrigin) {
        preloadLink.crossOrigin = crossOrigin;
      }

      // Insert before the first child or append if head is empty
      head.insertBefore(preloadLink, head.firstChild);
    }
  }

  /**
   * @description Sends a postMessage to Initiate the Q&A AI widget [For Test ]
   */
  setupQuestionsAndAnswersAIWidget(ecode: string, targetOrigin?: null | undefined | string): void {
    if (isPlatformBrowser(this.platformId)) {
      if (globalThis?.location && (!targetOrigin || targetOrigin === '')) {
        targetOrigin = globalThis.location.origin;
      }
      if (globalThis.parent) {
        if (targetOrigin !== undefined && targetOrigin !== null) {
          globalThis.parent.postMessage(ecode, targetOrigin);
        } else {
          globalThis.parent.postMessage(ecode);
        }
      }
    }
  }

  amountWithoutCurrency(amount: string | undefined) {
    if (!amount) {
      return '';
    }
    if (amount.includes('$')) {
      return amount.substring(1);
    }
    return amount;
  }

  /**
   * @description Verify whether the browser supports the Scheduler.yield function, enabling the execution to yield to the main thread and resume afterwards
   * @param fn the function that we want to execute without blocking main thread
   */
  async yieldTask(fn: () => void): Promise<void> {
    if (globalThis.scheduler?.yield) {
      await globalThis.scheduler.yield();
      fn();
    } else {
      let timeout = undefined;
      await new Promise<void>((resolve) => {
        timeout = setTimeout(() => {
          fn();
          resolve();
        }, 1);
      });
      clearTimeout(timeout);
    }
  }

  /**
   * @description Executes a callback when the browser is idle, using `requestIdleCallback` if supported.
   * Falls back to `setTimeout` when `requestIdleCallback` is not available.
   * Returns the native scheduling handle so callers can cancel pending work during cleanup.
   * @param callback - The function to be executed during idle time or after a delay.
   * @returns The native `requestIdleCallback` id or `setTimeout` handle.
   */
  runWhenIdle(callback: (idleData: IdleDeadline) => void): number | ReturnType<typeof setTimeout> {
    // Check if the browser supports requestIdleCallback natively
    if (globalThis.requestIdleCallback) {
      return globalThis.requestIdleCallback(callback);
    } else {
      // Fallback polyfill for unsupported browsers (e.g., Safari)
      const start = Date.now();

      return setTimeout(() => {
        // Simulate the IdleDeadline object expected by the callback
        callback({
          didTimeout: false, // Not simulated yet
          timeRemaining: () => Math.max(0, 50 - (Date.now() - start)), // Approximately 50ms "idle" budget
        });
      }, 1); // Small delay to mimic "run when idle"
    }
  }

  /**
   * @description Cancels a handle returned by `runWhenIdle`, regardless of whether it came from
   * `requestIdleCallback` or the `setTimeout` fallback.
   * @param handle - The handle returned by `runWhenIdle`.
   */
  cancelRunWhenIdle(handle: number | ReturnType<typeof setTimeout>): void {
    if (
      typeof requestIdleCallback !== 'undefined' &&
      typeof cancelIdleCallback !== 'undefined' &&
      typeof handle === 'number'
    ) {
      cancelIdleCallback(handle);
      return;
    }

    clearTimeout(handle);
  }

  /**
   * Defers the execution of a callback function if the page was rendered using speculation rules but
   * the user hasn't navigated to the page yet.
   *
   * - If running in a client-side, the callback is executed once the
   *   'prerenderingchange' event is fired on the document which means the user has navigated to the page.
   * - If running in a server-side using speculation rules prerendering, the callback is not executed.
   * - Otherwise, the callback is executed immediately.
   *
   * @param callback - The function to be executed when user navigates to the page or the page was not rendered using speculation rules.
   */
  deferOnPrerendering(callback: () => void): void {
    const isPrerenderSSR =
      !isPlatformBrowser(this.platformId) &&
      this.request?.headers?.['sec-purpose'] === 'prefetch;prerender';
    const isPrerenderCSR =
      isPlatformBrowser(this.platformId) &&
      'prerendering' in this.document &&
      this.document['prerendering'];

    if (isPrerenderSSR) {
      return;
    }

    if (isPrerenderCSR) {
      this.document.addEventListener('prerenderingchange', callback, { once: true });
    } else {
      callback();
    }
  }

  /**
   * Uses browser URL API to append query parameters to a given URL.
   * If the URL is invalid, it returns the original URL instead of throwing
   *
   * @param url the url to append the params to
   * @param params an object containing key/value pairs to append as query parameters
   * @returns the updated URL as a string or the original URL string if invalid
   */
  appendParamsToUrl(url: string, params: { [key: string]: string }): string {
    try {
      const urlObject = new URL(url);
      Object.keys(params).forEach((key) => {
        urlObject.searchParams.append(key, params[key]);
      });
      return urlObject.toString();
    } catch {
      return url;
    }
  }
}
